import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Divider,
  Avatar,
  Chip,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Business as UniversityIcon,
  Palette as BrandingIcon,
  MedicalServices as ComplaintsIcon,
  LocalHospital as SpecialtiesIcon,
  Description as TemplatesIcon,
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  MonetizationOn as BillingIcon,
  SmartToy as AiIcon
} from '@mui/icons-material';
import universityAPI from '../../api/university';
import PresetManager from '../../components/Admin/PresetManager';
import TemplateManager from '../../components/Admin/TemplateManager';
import useEntitlements from '../../hooks/useEntitlements';
import useProfileOverview from '../../hooks/useProfileOverview';
import useLicenseUsage from '../../hooks/useLicenseUsage';
import TenantAiSettingsPanel from '../../components/Admin/TenantAiSettingsPanel';
import UniversityAiBillingStatus from '../../components/Admin/UniversityAiBillingStatus';
import { tenantAdminAPI } from '../../services/api';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`university-tabpanel-${index}`}
      aria-labelledby={`university-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString();
};

const UniversitySettingsPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [universityData, setUniversityData] = useState({
    id: '',
    name: '',
    code: '',
    domains: [],
    logo: null,
    settings: {
      colors: {
        primary: '#1976d2',
        secondary: '#dc004e'
      }
    }
  });
  const [formData, setFormData] = useState({
    name: '',
    domains: '',
    primaryColor: '#1976d2'
  });
  const [aiConfiguration, setAiConfiguration] = useState(null);
  const [aiConfigurationLoading, setAiConfigurationLoading] = useState(false);
  const [aiConfigurationError, setAiConfigurationError] = useState(null);

  const { overview: profileOverview, loading: profileOverviewLoading } = useProfileOverview();
  const {
    status: entitlementStatus,
    loading: entitlementLoading,
    error: entitlementError,
    refresh: refreshEntitlements,
  } = useEntitlements({ enabled: activeTab === 5 });
  const {
    usage: licenseUsage,
    loading: licenseUsageLoading,
    error: licenseUsageError,
    refresh: refreshLicenseUsage,
  } = useLicenseUsage({ universityId: universityData.id, enabled: activeTab === 5 && Boolean(universityData.id) });

  const seatCapacity = licenseUsage?.seatCapacity
    ?? entitlementStatus?.seatSummary?.seatCapacity
    ?? universityData?.seatCapacity
    ?? 0;
  const seatsAllocated = licenseUsage?.seatsAllocated
    ?? entitlementStatus?.seatSummary?.seatsAllocated
    ?? 0;
  const utilisationDisplay = licenseUsage && licenseUsage.seatCapacity > 0
    ? `${Math.round((licenseUsage.seatsAllocated / licenseUsage.seatCapacity) * 100)}%`
    : '—';
  const unlimitedLicences = licenseUsage?.unlimitedLicenses ?? 0;
  const isTenantByok = aiConfiguration?.funding?.mode === 'tenant_byok';

  const loadUniversityData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await universityAPI.getMyUniversity();
      const data = response.data;
      
      setUniversityData({
        id: data.id,
        name: data.name,
        code: data.code,
        domains: data.domains || [],
        logo: data.logo,
        settings: data.settings || { colors: { primary: '#1976d2', secondary: '#dc004e' } }
      });

      setFormData({
        name: data.name,
        domains: (data.domains || []).join(', '),
        primaryColor: data.settings?.colors?.primary || '#1976d2'
      });
    } catch (error) {
      console.error('Error loading university data:', error);
      setAlert({ show: true, message: 'Failed to load university data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAiBillingConfiguration = useCallback(async () => {
    setAiConfigurationLoading(true);
    try {
      const response = await tenantAdminAPI.getAiConfiguration();
      setAiConfiguration(response.data || {});
      setAiConfigurationError(null);
    } catch (error) {
      setAiConfiguration(null);
      setAiConfigurationError(
        error.response?.data?.message || 'Unable to confirm AI billing ownership.',
      );
    } finally {
      setAiConfigurationLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUniversityData();
  }, [loadUniversityData]);

  useEffect(() => {
    if (activeTab === 5) {
      loadAiBillingConfiguration();
    }
  }, [activeTab, loadAiBillingConfiguration]);

  const showAlert = (message, severity = 'success') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity }), 5000);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const updateData = {
        name: formData.name.trim(),
        domains: formData.domains.split(',').map(d => d.trim()).filter(d => d),
        settings: {
          ...universityData.settings,
          colors: {
            ...universityData.settings.colors,
            primary: formData.primaryColor
          }
        }
      };

      await universityAPI.updateUniversity(universityData.id, updateData);
      
      // Reload data to get updated info
      await loadUniversityData();
      
      showAlert('University settings updated successfully');
    } catch (error) {
      console.error('Error updating university:', error);
      showAlert(error.response?.data?.message || 'Failed to update university settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      showAlert('Please select a PNG, JPEG, or WebP image', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showAlert('File size must be less than 5MB', 'error');
      return;
    }

    try {
      setUploadingLogo(true);
      
      const formData = new FormData();
      formData.append('logo', file);
      
      await universityAPI.uploadLogo(universityData.id, formData);
      
      // Reload data to get updated logo
      await loadUniversityData();
      
      showAlert('University logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      showAlert(error.response?.data?.message || 'Failed to upload logo', 'error');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading university settings...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <UniversityIcon sx={{ fontSize: 40, mr: 2, color: formData.primaryColor }} />
        <Box>
          <Typography variant="h3" component="h1">
            University Settings
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {universityData.name} ({universityData.code})
          </Typography>
        </Box>
      </Box>

      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            <Tab
              icon={<BrandingIcon />}
              label="University Info & Branding"
              id="university-tab-0"
              aria-controls="university-tabpanel-0"
            />
            <Tab
              icon={<ComplaintsIcon />}
              label="Presenting Complaints"
              id="university-tab-1"
              aria-controls="university-tabpanel-1"
            />
            <Tab
              icon={<SpecialtiesIcon />}
              label="Specialties"
              id="university-tab-2"
              aria-controls="university-tabpanel-2"
            />
            <Tab
              icon={<TemplatesIcon />}
              label="Templates"
              id="university-tab-3"
              aria-controls="university-tabpanel-3"
            />
            <Tab
              icon={<AiIcon />}
              label="AI Services"
              id="university-tab-4"
              aria-controls="university-tabpanel-4"
            />
            <Tab
              icon={<BillingIcon />}
              label="Billing & Licences"
              id="university-tab-5"
              aria-controls="university-tabpanel-5"
            />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    University Information
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="University Name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        variant="outlined"
                        helperText="Full name of your university"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="University Code"
                        value={universityData.code}
                        variant="outlined"
                        disabled
                        helperText="University code (read-only)"
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email Domains"
                        value={formData.domains}
                        onChange={(e) => handleInputChange('domains', e.target.value)}
                        variant="outlined"
                        helperText="Comma-separated list of email domains (e.g., university.edu, uni.ac.uk)"
                        placeholder="university.edu, uni.ac.uk"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Primary Brand Color"
                        type="color"
                        value={formData.primaryColor}
                        onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                        variant="outlined"
                        helperText="Primary color for your university branding"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
                
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ 
                      bgcolor: formData.primaryColor,
                      '&:hover': { 
                        bgcolor: formData.primaryColor,
                        filter: 'brightness(0.9)'
                      }
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    University Logo
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Box sx={{ textAlign: 'center' }}>
                    {universityData.logo ? (
                      <Avatar
                        src={universityData.logo}
                        sx={{ 
                          width: 120, 
                          height: 120, 
                          mx: 'auto', 
                          mb: 2,
                          border: '2px solid',
                          borderColor: 'divider'
                        }}
                      />
                    ) : (
                      <Avatar
                        sx={{ 
                          width: 120, 
                          height: 120, 
                          mx: 'auto', 
                          mb: 2,
                          bgcolor: formData.primaryColor,
                          fontSize: '2rem'
                        }}
                      >
                        {universityData.code}
                      </Avatar>
                    )}
                    
                    <input
                      accept="image/png,image/jpeg,image/webp"
                      style={{ display: 'none' }}
                      id="logo-upload"
                      type="file"
                      onChange={handleLogoUpload}
                    />
                    <label htmlFor="logo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        disabled={uploadingLogo}
                        fullWidth
                      >
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                    </label>
                    
                    <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                      Supported: JPEG, PNG, WebP (max 5MB)
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
              
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Domain Status
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  {universityData.domains && universityData.domains.length > 0 ? (
                    <Box>
                      {universityData.domains.map((domain, index) => (
                        <Chip
                          key={index}
                          label={domain}
                          variant="outlined"
                          size="small"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No domains configured
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <PresetManager type="complaints" title="Presenting Complaints" />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <PresetManager type="specialties" title="Specialties" />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <TemplateManager />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <TenantAiSettingsPanel />
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          {entitlementLoading || profileOverviewLoading || aiConfigurationLoading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : entitlementError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Unable to load billing overview</AlertTitle>
              {entitlementError.response?.data?.message || entitlementError.message}
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" onClick={refreshEntitlements}>Retry</Button>
              </Box>
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {aiConfigurationError && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    <AlertTitle>Unable to confirm AI billing ownership</AlertTitle>
                    {aiConfigurationError}
                    <Box sx={{ mt: 1 }}>
                      <Button variant="outlined" size="small" onClick={loadAiBillingConfiguration}>
                        Retry
                      </Button>
                    </Box>
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <UniversityAiBillingStatus
                  configuration={aiConfiguration}
                  historyCostTokens={profileOverview?.billing?.billingSettings?.historyCostTokens ?? 0}
                  paymentRequired={Boolean(
                    entitlementStatus?.requiresPayment || entitlementStatus?.paymentRequired
                  )}
                  walletBalance={entitlementStatus?.wallet?.balance ?? 0}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Licence utilisation</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Typography variant="body2">Seat capacity: {seatCapacity}</Typography>
                      <Typography variant="body2">Seats allocated: {seatsAllocated}</Typography>
                      <Typography variant="body2">Utilisation: {utilisationDisplay}</Typography>
                      <Typography variant="body2">Unlimited licences: {unlimitedLicences}</Typography>
                      {profileOverview?.licenses?.summary && (
                        <Typography variant="body2">
                          Active licences: {profileOverview.licenses.summary.activeLicenses} / {profileOverview.licenses.summary.totalLicenses}
                        </Typography>
                      )}
                    </Stack>
                    {licenseUsageLoading ? (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <CircularProgress size={20} />
                        <Typography variant="body2">Loading licence usage…</Typography>
                      </Stack>
                    ) : licenseUsageError ? (
                      <Alert severity="error">
                        <Typography variant="body2">
                          {licenseUsageError.response?.data?.message || licenseUsageError.message}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Button variant="outlined" size="small" onClick={refreshLicenseUsage}>Retry</Button>
                        </Box>
                      </Alert>
                    ) : Array.isArray(licenseUsage?.licenses) && licenseUsage.licenses.length > 0 ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Seats</TableCell>
                            <TableCell align="right">Allocated</TableCell>
                            <TableCell align="right">Utilisation</TableCell>
                            <TableCell>Expires</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {licenseUsage.licenses.map((license) => {
                            const seatLimitLabel = license.seatLimit > 0 ? license.seatLimit : 'Unlimited';
                            const utilisation = license.seatLimit > 0 && license.seatLimit
                              ? `${Math.round((license.seatsAllocated / license.seatLimit) * 100)}%`
                              : '—';
                            return (
                              <TableRow key={license.id || license.name}>
                                <TableCell>{license.name}</TableCell>
                                <TableCell>{license.type || '—'}</TableCell>
                                <TableCell>{license.status || '—'}</TableCell>
                                <TableCell align="right">{seatLimitLabel}</TableCell>
                                <TableCell align="right">{license.seatsAllocated ?? 0}</TableCell>
                                <TableCell align="right">{utilisation}</TableCell>
                                <TableCell>{formatDateTime(license.expiresAt)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No licences configured for this university.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Account invoices</Typography>
                  {isTenantByok && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      AI provider invoices are managed directly with your configured providers and are not shown here.
                    </Typography>
                  )}
                  {Array.isArray(entitlementStatus?.invoices) && entitlementStatus.invoices.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Due date</TableCell>
                          <TableCell>Amount due</TableCell>
                          <TableCell>Link</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {entitlementStatus.invoices.slice(0, 6).map((invoice) => (
                          <TableRow key={invoice.invoiceId}>
                            <TableCell>{invoice.invoiceId}</TableCell>
                            <TableCell>{invoice.status}</TableCell>
                            <TableCell>{invoice.dueDate ? formatDateTime(invoice.dueDate) : '—'}</TableCell>
                            <TableCell>
                              {invoice.amountDue ? `${invoice.amountDue / 100} ${invoice.currency?.toUpperCase()}` : '—'}
                            </TableCell>
                            <TableCell>
                              {invoice.hostedInvoiceUrl ? (
                                <Link href={invoice.hostedInvoiceUrl} target="_blank" rel="noopener">
                                  View
                                </Link>
                              ) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No invoices recorded yet.
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {!isTenantByok && Array.isArray(entitlementStatus?.messages) && entitlementStatus.messages.length > 0 && (
                <Grid item xs={12}>
                  <Stack spacing={2}>
                    {entitlementStatus.messages.map((msg) => (
                      <Alert key={msg.id} severity={msg.severity || 'info'}>
                        <AlertTitle>{msg.title}</AlertTitle>
                        {msg.body}
                      </Alert>
                    ))}
                  </Stack>
                </Grid>
              )}
            </Grid>
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default UniversitySettingsPage;
